import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import {
  AddButton,
  ExportExcelButton,
} from "share-components/mui-wrap/button/action-button";
import { PageContentContainer } from "share-components/templates/page/page-content-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";

import { PurchaseOrdersPermission } from "commons/enum/permission";
import { Dayjs } from "dayjs";
import { DateRange } from "share-components/atomics/date-range";
import { ProductGroupSelection } from "share-components/group-selection/product-group-selection/product-group-selection";
import { ProductGroupSelectionKey } from "share-components/group-selection/product-group-selection/use-product-group-selection";
import { XAutoCompleteWithPagination } from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";
import { SearchButton } from "share-components/mui-wrap/button/search-button";
import { XSearchTextField } from "share-components/mui-wrap/text-field/x-search-text-field";
import { PageFilterContainer } from "share-components/templates/page/page-filter-container";
import { AddOrder } from "../add-order/add-order";
import { ExportExcel } from "../export-excel/export-excel";
import { searchOrderPONumberOptionsWithPagination } from "./order-po-number-options";
import { OrderTable } from "./order-table";

export const OrderList = observer(() => {
  const {
    orderManagement: {
      openNew,
      openExportExcel,
      getOrders,
      pagination,
      queryParams: {
        poDateRangeFilter: {
          setProp: setPODateRange,
          fromAsDayJs: fromPODayJs,
          toAsDayJs: toPODayJs,
        },
        deliveryDateRangeFilter: {
          setProp: setDeliveryDateRangeFilter,
          fromAsDayJs: fromDeliDayJs,
          toAsDayJs: toDeliDayjs,
        },
        code,
        name,
        dealingName,
        pONumber,
        barcode,
        setProp: setQueryParamProp,
      },
    },
  } = useStores();
  const onSearch = useCallback(async () => {
    pagination.setProp("pageIndex", 0);
    await getOrders();
  }, [getOrders, pagination]);

  const fromPODateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setPODateRange("from", value ? value.toString() : null);
    },
    [setPODateRange],
  );

  const toPODateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setPODateRange("to", value ? value.toString() : null);
    },
    [setPODateRange],
  );

  const fromDeliDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDeliveryDateRangeFilter("from", value ? value.toString() : null);
    },
    [setDeliveryDateRangeFilter],
  );

  const toDeliDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDeliveryDateRangeFilter("to", value ? value.toString() : null);
    },
    [setDeliveryDateRangeFilter],
  );

  const setProductFilterProp = useCallback(
    (prop: ProductGroupSelectionKey, value: string) => {
      setQueryParamProp(prop as "dealingName" | "name" | "code", value);
    },
    [setQueryParamProp],
  );

  return (
    <>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <Stack direction={"column"} spacing={3}>
        <PageFilterContainer>
          <DateRange
            label={"Ngày PO"}
            fromDate={fromPODayJs}
            toDate={toPODayJs}
            onFromDateChanged={fromPODateChangedHandler}
            onToDateChanged={toPODateChangedHandler}
          ></DateRange>

          <DateRange
            label={"Ngày giao"}
            fromDate={fromDeliDayJs}
            toDate={toDeliDayjs}
            onFromDateChanged={fromDeliDateChangedHandler}
            onToDateChanged={toDeliDateChangedHandler}
          ></DateRange>
        </PageFilterContainer>

        <PageFilterContainer>
          <ProductGroupSelection
            dealingNameSelected={dealingName}
            nameSelected={name}
            codeSelected={code}
            setSelectionProp={setProductFilterProp}
            searchWhenRender={false}
            visibleFields={["dealingName", "name", "code"]}
          />

          <XSearchTextField
            label="Mã barcode"
            value={barcode}
            onChange={(e) => setQueryParamProp("barcode", e.currentTarget.value)}
          />

          <Box sx={{ minWidth: 220 }}>
            <XAutoCompleteWithPagination
              label="Số PO"
              value={pONumber}
              searchFunc={searchOrderPONumberOptionsWithPagination}
              searchOnFocus={true}
              searchWhenRender={false}
              onValueChanged={(v) => {
                setQueryParamProp("pONumber", v?.value ?? "");
              }}
            />
          </Box>
          <SearchButton asyncAction={onSearch}></SearchButton>
        </PageFilterContainer>
      </Stack>
      <PageContentContainer>
        <OrderTable></OrderTable>
      </PageContentContainer>

      <>{openNew && <AddOrder></AddOrder>}</>
      <>{openExportExcel && <ExportExcel></ExportExcel>}</>
    </>
  );
});

const Header = observer(() => {
  const {
    orderManagement: {
      setProp,
      queryParams: { showSuccess, setProp: setQueryProps },
      getOrders,
    },
  } = useStores();
  const showSuccessHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setQueryProps("showSuccess", checked);
      await getOrders();
    },
    [getOrders, setQueryProps],
  );
  return (
    <>
      <Stack>
        <Typography variant="subtitle2">Quản lý đơn đặt hàng</Typography>
      </Stack>
      <Stack direction={"row"} spacing={1}>
        <FormControlLabel
          control={
            <Checkbox checked={showSuccess} onChange={showSuccessHandler} />
          }
          label="Bao gồm hoàn thành"
        />
        <ExportExcelButton
          accessPermission={[PurchaseOrdersPermission.ExportExcel]}
          onClick={() => {
            setProp("openExportExcel", true);
          }}
        ></ExportExcelButton>
        <AddButton
          accessPermission={[PurchaseOrdersPermission.Create]}
          onClick={() => {
            setProp("openNew", true);
          }}
        >
          Thêm mới
        </AddButton>
      </Stack>
    </>
  );
});
